import { useEffect, useState } from "react";
import { Repeat, Pause, Play, Trash } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { Panel, Field, Badge } from "@/components/common";
import { Sel } from "@/components/Sel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chips } from "@/components/studio/kit";
import { DayTimePicker, fromISO, toISO } from "@/components/studio/DayTimePicker";

const Row = ({ s, reload }) => {
  const upd = async (body, ok) => { await act(() => api.put(`/studio/series/${s.id}`, body), ok); reload(); };
  const del = async () => { await act(() => api.delete(`/studio/series/${s.id}`), "Series deleted"); reload(); };
  return (
    <div className="py-2.5 flex items-center gap-3" data-testid={`series-row-${s.id}`}>
      <Repeat className="text-[#007AFF]" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{s.name} <span className="text-zinc-500 font-normal">· every {s.interval_days} days</span></div>
        <div className="text-xs text-zinc-500 truncate">Next issue {fmtDate(s.next_send_at)} · topic {Math.min(s.next_index + 1, s.topics.length)}/{s.topics.length}: {s.topics[s.next_index] || "— add topics"}</div>
      </div>
      <Badge v={s.active ? "active" : "paused"} />
      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`series-toggle-${s.id}`} onClick={() => upd({ active: !s.active }, s.active ? "Paused" : "Resumed")}>{s.active ? <Pause /> : <Play />}</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`series-delete-${s.id}`} onClick={del}><Trash /></Button>
    </div>
  );
};

export const SeriesPanel = ({ products, segs }) => {
  const { data, reload } = useFetch("/studio/series");
  const { data: bt } = useFetch("/studio/best-times?kind=newsletter");
  const [f, setF] = useState({ name: "", topics: "", interval_days: 14, style: "photo", first: { day: null, time: "08:00" } });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  useEffect(() => { if (!f.product_id && products[0]) set("product_id", products[0].id); }, [products.length]); // eslint-disable-line
  useEffect(() => { if (!f.segment_id && segs?.[0]) set("segment_id", segs[0].id); }, [segs]); // eslint-disable-line
  const create = async () => {
    const body = { ...f, topics: f.topics.split("\n").map((t) => t.trim()).filter(Boolean), first_send_at: toISO(f.first) };
    const r = await act(() => api.post("/studio/series", body), "Series saved — each issue is drafted 2 days early for your approval");
    if (r) { setF({ ...f, name: "", topics: "", first: { day: null, time: "08:00" } }); reload(); }
  };
  return (
    <Panel title="Repeat series" data-testid="series-panel">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Series name"><Input data-testid="series-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Partner Brief" className="bg-black" /></Field>
          <Field label="Every"><Chips value={`${f.interval_days} days`} onChange={(v) => set("interval_days", parseInt(v, 10))} options={["7 days", "14 days", "28 days"]} testid="series-interval" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Product"><Sel testid="series-product-select" value={f.product_id} onChange={(v) => set("product_id", v)} options={products.map((p) => ({ value: p.id, label: p.name }))} /></Field>
          <Field label="Segment"><Sel testid="series-segment-select" value={f.segment_id} onChange={(v) => set("segment_id", v)} options={(segs || []).map((s) => ({ value: s.id, label: s.name }))} /></Field>
        </div>
        <Field label="Topic list (one per line, used in order)"><Textarea rows={4} data-testid="series-topics-input" value={f.topics} onChange={(e) => set("topics", e.target.value)} placeholder={"EOFY document checklist\nOnboarding clients without chasing\nWhat partners do with 10 extra hours"} className="bg-black text-sm" /></Field>
        <Field label="First issue goes out" hint={bt?.basis}>
          <DayTimePicker value={f.first} onChange={(v) => set("first", v)} testid="series-first" />
          <div className="flex gap-1.5 mt-1.5 flex-wrap">{(bt?.slots || []).map((s) => (
            <button key={s.at} type="button" data-testid={`series-suggest-${s.label.replace(/\s/g, "-")}`} onClick={() => set("first", fromISO(s.at))} className="font-mono text-[11px] px-2 py-0.5 rounded-full border border-[#007AFF]/50 text-[#7FB8FF] hover:bg-[#007AFF]/10">{s.label}</button>
          ))}</div>
        </Field>
        <Button size="sm" data-testid="series-create-btn" disabled={!f.product_id || !f.segment_id || !f.topics.trim() || !f.first.day} onClick={create} className="bg-[#007AFF] hover:bg-[#3395FF]">Save series</Button>
        <div className="divide-y divide-border/60 border-t border-border">{(data || []).filter((s) => !s.is_deleted).map((s) => <Row key={s.id} s={s} reload={reload} />)}</div>
      </div>
    </Panel>
  );
};
